import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

type TournamentPhase = 'groups' | 'elimination' | 'final' | 'finished';

const normalizePhase = (phase: string): string => {
  if (!phase) return '';
  const p = phase.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (p === 'qf' || p.startsWith('cuarto')) return 'cuartos';
  if (p === 'sf' || p.startsWith('semi')) return 'semifinal';
  if (p === 'f' || p === 'final') return 'final';
  if (p.startsWith('octavo') || p === 'r16') return 'octavos';
  return p;
};

interface TournamentProgress {
  phase: TournamentPhase;
  progress: number;
  loading: boolean;
}

export function useTournamentProgress(
  eventId: string,
  category?: string | null,
  subcategory?: string | null
) {
  const [progress, setProgress] = useState<TournamentProgress>({
    phase: 'groups',
    progress: 33,
    loading: true
  });

  useEffect(() => {
    async function fetchProgress() {
      try {
        const { data: event } = await supabase
          .from('events')
          .select('status')
          .eq('id', eventId)
          .single();

        if (!event) {
          setProgress({ phase: 'groups', progress: 33, loading: false });
          return;
        }

        if (event.status === 'completed') {
          setProgress({ phase: 'finished', progress: 100, loading: false });
          return;
        }

        // Check elimination matches filtered by category/subcategory
        let elimQuery = supabase
          .from('tournament_match_results')
          .select('phase')
          .eq('tournament_id', eventId)
          .in('phase', ['octavos', 'cuartos', 'semifinal', 'final']);

        if (category) elimQuery = elimQuery.eq('category', category);
        if (subcategory) elimQuery = elimQuery.eq('subcategory', subcategory);

        const { data: allMatches } = await elimQuery;

        const phases = allMatches?.map(m => m.phase) || [];
        const hasFinal = phases.includes('final');
        const hasSemifinal = phases.includes('semifinal');

        if (hasFinal || hasSemifinal) {
          setProgress({ phase: 'final', progress: 100, loading: false });
          return;
        }

        // Check if there's a scheduled final/semifinal match in tournament_schedules
        const { data: schedData } = await (supabase as any)
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', eventId)
          .limit(1);

        if (schedData?.[0]?.schedule_data) {
          const sd = schedData[0].schedule_data as Record<string, any>;
          for (const [, dayData] of Object.entries(sd)) {
            if (dayData?.individualMatches) {
              const hasScheduledFinal = (dayData.individualMatches as any[]).some((m: any) => {
                const norm = normalizePhase(m.phase || '');
                const matchesCategory = !category || (m.category || '').includes(category);
                return (norm === 'final' || norm === 'semifinal') && matchesCategory;
              });
              if (hasScheduledFinal) {
                setProgress({ phase: 'final', progress: 100, loading: false });
                return;
              }
            }
          }
        }

        if (phases.length > 0) {
          setProgress({ phase: 'elimination', progress: 66, loading: false });
          return;
        }

        // Check if elimination seeding exists (groups finished, elimination started)
        let seedingQuery = supabase
          .from('tournament_elimination_seeding')
          .select('id')
          .eq('tournament_id', eventId)
          .limit(1);

        if (category) seedingQuery = seedingQuery.eq('category', category);
        if (subcategory) seedingQuery = seedingQuery.eq('subcategory', subcategory);

        const { data: seedingData } = await seedingQuery;

        if (seedingData && seedingData.length > 0) {
          setProgress({ phase: 'elimination', progress: 66, loading: false });
          return;
        }

        // Check if group phase is done by looking for group results
        let groupQuery = supabase
          .from('tournament_match_results')
          .select('id')
          .eq('tournament_id', eventId)
          .eq('phase', 'grupo')
          .limit(1);

        if (category) groupQuery = groupQuery.eq('category', category);
        if (subcategory) groupQuery = groupQuery.eq('subcategory', subcategory);

        const { data: groupResults } = await groupQuery;

        if (groupResults && groupResults.length > 0) {
          // There are group results; check if elimination matches exist in schedules
          let schedQuery = supabase
            .from('tournament_match_results')
            .select('id')
            .eq('tournament_id', eventId)
            .in('phase', ['octavos', 'cuartos', 'semifinal', 'final']);

          if (category) schedQuery = schedQuery.eq('category', category);
          if (subcategory) schedQuery = schedQuery.eq('subcategory', subcategory);

          // Already checked above and found none, so stay at groups for now
          // But if all group matches are completed, advance to elimination
          // Check if there are any pending group matches (no score yet)
          // For simplicity: if group results exist, we consider groups phase active at 33%
        }

        setProgress({ phase: 'groups', progress: 33, loading: false });
      } catch (error) {
        console.error('[useTournamentProgress] Error:', error);
        setProgress({ phase: 'groups', progress: 33, loading: false });
      }
    }

    fetchProgress();

    const channelSuffix = [eventId, category, subcategory].filter(Boolean).join('-');
    const channel = supabase
      .channel(`tournament-progress-${channelSuffix}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tournament_match_results',
        filter: `tournament_id=eq.${eventId}`
      }, () => {
        fetchProgress();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tournament_elimination_seeding',
        filter: `tournament_id=eq.${eventId}`
      }, () => {
        fetchProgress();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'tournament_schedules',
        filter: `tournament_id=eq.${eventId}`
      }, () => {
        fetchProgress();
      })
      .subscribe();

    const pollInterval = setInterval(() => {
      fetchProgress();
    }, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [eventId, category, subcategory]);

  return progress;
}
